
import type { AuctionBoard, AuctionLot, AuctionNews } from "../net/messages";
import type { BagModel } from "./bag";
import { money } from "../money";
import { itemArtNode } from "./itemart";
import { ItemTooltip, attachHoverTooltip, wireTooltipData, type ItemTooltipData } from "./tooltip";
import { elem, styleInstaller } from "./chrome";

/** `AuctionLot.claim`, mirrored here so this file reads without importing the
 *  connection. The numbers are the server's (`auction.CLAIM_*`); this is a
 *  label table, never a decision. */
export const CLAIM_NOTHING = 0;
export const CLAIM_ITEM = 1;
export const CLAIM_PROCEEDS = 2;
export const CLAIM_WITHDRAW = 3;
export const CLAIM_REFUND = 4;

/** `AuctionNews.event`. */
export const NEWS_OUTBID = 1;
export const NEWS_SOLD = 2;

export interface AuctionActions {
  /** Ask for a fresh board. Also what the panel does when it opens. */
  onRefresh?: () => void;
  /** List the item in this bag cell. `buyNowPrice` of zero means none. */
  onList?: (sourceSlot: number, startingBid: number, buyNowPrice: number) => void;
  /** Bid `total` -- a whole escrow, not an amount to add -- on this lot. */
  onBid?: (slot: number, total: number, boardRevision: number) => void;
  /** End this lot at its fixed price. */
  onBuyNow?: (slot: number, boardRevision: number) => void;
  /** Take whatever this rendered row owes you. The server decides what that is. */
  onClaim?: (slot: number, boardRevision: number) => void;
}

export function parseAmount(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value) || value < 1) return null;
  return value;
}

export function parseOptionalAmount(text: string): number | null {
  if (text.trim() === "") return 0;
  return parseAmount(text);
}

export function describeTimeLeft(seconds: number): string {
  if (seconds <= 0) return "closed";
  if (seconds < 60) return `${seconds}s left`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m left`;
  return `${Math.floor(seconds / 3600)}h left`;
}

export function claimLabel(claim: number): string | null {
  switch (claim) {
    case CLAIM_ITEM:
      return "Collect";
    case CLAIM_PROCEEDS:
      // The currency is always "Yang" in the shipped client (`MONETARY_UNIT0`,
      // `locale_game.txt:304`), never "gold" -- the auction house itself has
      // no 2014 locale text (it postdates this snapshot), but its currency
      // word should still be the shipped one.
      return "Take the Yang";
    case CLAIM_WITHDRAW:
      return "Withdraw";
    case CLAIM_REFUND:
      return "Reclaim your bid";
    default:
      return null;
  }
}

/** One line of news, for the chat log. An event, not a state -- see
 *  `AuctionNews`. `other_name` is another player's name and reaches the DOM as
 *  text and nothing else, which is what the caller must do with this string. */
export function describeNews(news: AuctionNews): string {
  const who = news.other_name === "" ? "Somebody" : news.other_name;
  if (news.event === NEWS_SOLD) {
    return `${who} bought your ${news.name} for ${news.amount}. Collect the Yang from the auction house.`;
  }
  if (news.event === NEWS_OUTBID) {
    return `${who} has bid ${news.amount} on ${news.name}. Your own bid is waiting for you to reclaim it.`;
  }
  return `Something happened to lot ${news.lot}.`;
}

export function auctionable(slot: { readonly vnum: number } | undefined): boolean {
  return slot !== undefined && slot.vnum > 0;
}

const STYLE_ID = "auction-style";

const STYLE = `
.auction {
  position: fixed;
  z-index: 41;
  left: 50%;
  transform: translateX(-50%);
  top: max(72px, calc(env(safe-area-inset-top) + 72px));
  width: min(460px, 94vw);
  max-height: min(72vh, calc(100vh - 180px));
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(170, 220, 255, 0.3);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 12px/1.45 system-ui, sans-serif;
  color: #d8dee6;
}
.auction-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.auction-title { color: #bfe3ff; font-weight: 600; }
.auction-close, .auction-refresh {
  border: 0;
  background: transparent;
  color: #9b9484;
  font: inherit;
  cursor: pointer;
  padding: 0 2px;
}
.auction-close:hover, .auction-refresh:hover { color: #bfe3ff; }
.auction-tabs { display: flex; gap: 5px; margin: 0 0 8px; }
.auction-tab {
  padding: 4px 9px; border: 1px solid rgba(170, 220, 255, 0.24);
  border-radius: 6px; color: #aeb9c6; background: rgba(170, 220, 255, 0.04);
  font: inherit; cursor: pointer;
}
.auction-tab[aria-selected="true"] {
  border-color: rgba(170, 220, 255, 0.62); color: #eaf4ff;
  background: rgba(170, 220, 255, 0.16);
}
.auction-list { overflow-y: auto; display: flex; flex-direction: column; gap: 5px; }
.auction-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px;
  border-radius: 7px;
  border: 1px solid rgba(170, 220, 255, 0.18);
  background: rgba(170, 220, 255, 0.05);
}
.auction-row-mine { border-color: rgba(255, 210, 122, 0.35); }
.auction-row-top { display: flex; align-items: center; gap: 8px; }
/* Each list line's name sits in Parameter_Slot_04, the 115x18 plate
   __MakeListPage puts at x=55 of every row (uiauction.py:71-74). */
.auction-row-name {
  flex: 0 0 auto;
  width: 115px;
  height: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 0 4px;
  box-sizing: border-box;
  line-height: 18px;
  background: url("/ui/textures/public/parameter_slot_04.png") no-repeat left top;
  image-rendering: pixelated;
}
.auction-row-clock { color: #9b9484; font-variant-numeric: tabular-nums; }
.auction-row-bottom { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.auction-price { color: #ffd27a; font-variant-numeric: tabular-nums; }
.auction-seller { color: #9b9484; }
.auction-yours { color: #8fd6a0; }
.auction-glyph { width: 18px; height: 18px; flex: 0 0 auto; }
.auction-row .item-icon { height: 22px; margin: 0; flex: 0 0 auto; }
.auction-empty { color: #9b9484; padding: 6px 2px; }
.auction-sell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.auction-sell-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.auction-input, .auction-pick {
  border-radius: 6px;
  border: 1px solid rgba(170, 220, 255, 0.28);
  background: rgba(0, 0, 0, 0.35);
  color: inherit;
  font: inherit;
  padding: 4px 6px;
}
.auction-input { width: 92px; text-align: right; }
.auction-pick { flex: 1 1 140px; }
.auction-button {
  padding: 4px 9px;
  border-radius: 7px;
  border: 1px solid rgba(170, 220, 255, 0.35);
  background: rgba(170, 220, 255, 0.12);
  color: #eaf4ff;
  font: inherit;
  cursor: pointer;
}
.auction-button[disabled] { opacity: 0.4; cursor: default; }
.auction-claim { border-color: rgba(143, 214, 160, 0.5); background: rgba(143, 214, 160, 0.14); }
.auction-hint { color: #9b9484; font-size: 0.92em; }

@media (max-width: 620px) {
  .auction { width: 96vw; max-height: 76vh; }
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

function lotTooltipData(lot: AuctionLot): ItemTooltipData {
  return wireTooltipData(lot, { price: lot.current_bid });
}

export class AuctionPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly sell: HTMLElement;
  private readonly browseTab: HTMLButtonElement;
  private readonly sellTab: HTMLButtonElement;
  private readonly pick: HTMLSelectElement;
  private readonly startInput: HTMLInputElement;
  private readonly buyNowInput: HTMLInputElement;
  private readonly listButton: HTMLButtonElement;
  private readonly hint: HTMLElement;
  private board: AuctionBoard | null = null;
  private bag: BagModel | null = null;
  private readonly drafts = new Map<number, string>();
  private readonly clocks = new Map<AuctionLot, HTMLElement>();
  private readonly tooltip: ItemTooltip;

  constructor(
    container: HTMLElement,
    private readonly actions: AuctionActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "auction");
    this.root.hidden = true;

    const header = elem(doc, "div", "auction-header");
    const title = elem(doc, "span", "auction-title", "Auction house");
    const refresh = doc.createElement("button");
    refresh.type = "button";
    refresh.className = "auction-refresh";
    // Shipped as a capitalized word wherever the client refreshes a list
    // (`locale_interface.txt` `GUILD_BOARD_REFRESH`/`MARKLIST_REFRESH`), not
    // lowercase house style.
    refresh.textContent = "Refresh";
    refresh.addEventListener("click", () => this.actions.onRefresh?.());
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "auction-close";
    // `CLOSE` (`locale_interface.txt:16`), matching `refine.ts`'s and
    // `cube.ts`'s own Cancel/Close buttons rather than a lowercase house style.
    close.textContent = "Close";
    close.addEventListener("click", () => this.hide());
    header.append(title, refresh, close);

    // `AuctionWindow.SelectPage` exposes its browse and registration surfaces
    // as mutually exclusive pages (`uiauction.py:22-51,135-149`).  Its third
    // native page only draws temporary placeholder strings (`:96-132`), so the
    // browser faithfully exposes the two pages backed by this shard's frames.
    const tabs = elem(doc, "div", "auction-tabs");
    tabs.setAttribute("role", "tablist");
    this.browseTab = doc.createElement("button");
    this.browseTab.type = "button";
    this.browseTab.className = "auction-tab";
    this.browseTab.textContent = "Browse";
    this.browseTab.dataset.auctionPage = "browse";
    this.browseTab.setAttribute("role", "tab");
    this.browseTab.addEventListener("click", () => this.selectPage("browse"));
    this.sellTab = doc.createElement("button");
    this.sellTab.type = "button";
    this.sellTab.className = "auction-tab";
    this.sellTab.textContent = "List item";
    this.sellTab.dataset.auctionPage = "sell";
    this.sellTab.setAttribute("role", "tab");
    this.sellTab.addEventListener("click", () => this.selectPage("sell"));
    tabs.append(this.browseTab, this.sellTab);

    this.list = elem(doc, "div", "auction-list");

    this.sell = elem(doc, "div", "auction-sell");
    const row = elem(doc, "div", "auction-sell-row");
    this.pick = elem(doc, "select", "auction-pick");
    this.startInput = elem(doc, "input", "auction-input");
    this.startInput.type = "text";
    this.startInput.inputMode = "numeric";
    this.startInput.placeholder = "opening bid";
    this.buyNowInput = elem(doc, "input", "auction-input");
    this.buyNowInput.type = "text";
    this.buyNowInput.inputMode = "numeric";
    this.buyNowInput.placeholder = "buy now";
    this.listButton = doc.createElement("button");
    this.listButton.type = "button";
    this.listButton.className = "auction-button";
    this.listButton.textContent = "List";
    this.listButton.addEventListener("click", () => this.submitListing());
    row.append(this.pick, this.startInput, this.buyNowInput, this.listButton);

    this.hint = elem(doc, "div", "auction-hint");
    this.hint.textContent =
      "A lot runs for a day. Leave the buy-now box empty for no fixed price.";
    this.sell.append(row, this.hint);

    this.root.append(header, tabs, this.list, this.sell);
    container.append(this.root);
    this.selectPage("browse");
  }

  /** The bag, for the seller's half. Redrawn on every `Bag` frame, because a
   *  cell that emptied is a cell that must stop being offered. */
  setBag(bag: BagModel): void {
    this.bag = bag;
    if (this.showing) this.paintBag();
  }

  update(board: AuctionBoard): void {
    this.board = board;
    if (this.showing) this.paintBoard();
  }

  /** Open, and ask for a board. The request is what makes the panel's contents
   *  meaningful: an empty `lots` means "not asked" until it has been. */
  open(): void {
    this.root.hidden = false;
    this.paintBoard();
    this.paintBag();
    this.actions.onRefresh?.();
  }

  hide(): void {
    this.root.hidden = true;
    this.tooltip.hide();
  }

  toggle(): void {
    if (this.showing) this.hide();
    else this.open();
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** Switch the two server-backed pages without rebuilding either workflow. */
  private selectPage(page: "browse" | "sell"): void {
    const browse = page === "browse";
    this.list.hidden = !browse;
    this.sell.hidden = browse;
    this.browseTab.setAttribute("aria-selected", String(browse));
    this.sellTab.setAttribute("aria-selected", String(!browse));
  }

  tick(): void {
    if (!this.showing || this.board === null) return;
    for (const lot of this.board.lots) {
      if (lot.seconds_left <= 0) continue;
      lot.seconds_left -= 1;
      const clock = this.clocks.get(lot);
      if (clock !== undefined) clock.textContent = describeTimeLeft(lot.seconds_left);
    }
  }

  private paintBoard(): void {
    const board = this.board;
    const doc = this.root.ownerDocument;
    this.list.replaceChildren();
    this.clocks.clear();
    if (board === null || board.lots.length === 0) {
      const empty = elem(doc, "div", "auction-empty", "Nothing is up for auction.");
      this.list.append(empty);
      return;
    }
    for (const [slot, lot] of board.lots.entries()) {
      this.list.append(this.rowFor(lot, slot));
    }
  }

  private rowFor(lot: AuctionLot, slot: number): HTMLElement {
    const doc = this.root.ownerDocument;
    const row = elem(doc, "div", `auction-row${lot.mine ? " auction-row-mine" : ""}`);

    const top = elem(doc, "div", "auction-row-top");
    const art = itemArtNode(lot.vnum, lot.kind, doc, "auction-glyph");
    const name = elem(doc, "span", "auction-row-name");
    // Player-authored text nowhere near markup: the seller's name is a string
    // another person chose, and `textContent` is the only thing this file ever
    // does with one.
    name.textContent = lot.count > 1 ? `${lot.name} x${lot.count}` : lot.name;
    const clock = elem(doc, "span", "auction-row-clock", describeTimeLeft(lot.seconds_left));
    this.clocks.set(lot, clock);
    top.append(art, name, clock);
    attachHoverTooltip(top, this.tooltip, () => lotTooltipData(lot));

    const bottom = elem(doc, "div", "auction-row-bottom");
    const price = elem(doc, "span", "auction-price", money(lot.current_bid));
    const seller = elem(doc, "span", "auction-seller", lot.mine ? "your lot" : lot.seller_name);
    bottom.append(price, seller);

    if (lot.your_escrow > 0) {
      const yours = elem(doc, "span", "auction-yours");
      yours.textContent = lot.winning
        ? `winning with ${lot.your_escrow}`
        : `you bid ${lot.your_escrow}`;
      bottom.append(yours);
    }

    // Bidding, only while the clock is running. The input is seeded with the
    // server's own `minimum_bid` -- the integer ceiling of the five-percent
    // ladder (`AuctionManager.cpp:489`) -- rather than computed here, so the
    // number a player sees is the number the server will accept.
    if (lot.seconds_left > 0) {
      const amount = elem(doc, "input", "auction-input");
      amount.type = "text";
      amount.inputMode = "numeric";
      amount.placeholder = String(lot.minimum_bid);
      amount.value = this.drafts.get(lot.id) ?? "";
      amount.addEventListener("input", () => this.drafts.set(lot.id, amount.value));
      const bid = doc.createElement("button");
      bid.type = "button";
      bid.className = "auction-button";
      bid.textContent = "Bid";
      bid.addEventListener("click", () => {
        // An empty box means the minimum, which is what a player who clicked
        // "Bid" without typing anything meant. Anything else that is not a
        // number is left alone rather than coerced -- the server would refuse
        // it and the refusal would be about a number nobody typed.
        const typed = amount.value.trim();
        const total = typed === "" ? lot.minimum_bid : parseAmount(typed);
        if (total === null) return;
        this.drafts.delete(lot.id);
        this.actions.onBid?.(slot, total, this.board?.revision ?? 0);
      });
      bottom.append(amount, bid);

      if (lot.buy_now_price > 0) {
        const buy = doc.createElement("button");
        buy.type = "button";
        buy.className = "auction-button";
        buy.textContent = `Buy now ${money(lot.buy_now_price)}`;
        buy.addEventListener("click", () => this.actions.onBuyNow?.(slot, this.board?.revision ?? 0));
        bottom.append(buy);
      }
    }

    const label = claimLabel(lot.claim);
    if (label !== null) {
      const claim = doc.createElement("button");
      claim.type = "button";
      claim.className = "auction-button auction-claim";
      claim.textContent = label;
      claim.addEventListener("click", () =>
        this.actions.onClaim?.(slot, this.board?.revision ?? 0),
      );
      bottom.append(claim);
    }

    row.append(top, bottom);
    return row;
  }

  get draft(): { sourceSlot: number; startingBid: number; buyNowPrice: number } | null {
    const cell = this.pick.value;
    if (cell === "") return null;
    const startingBid = parseAmount(this.startInput.value);
    const buyNowPrice = parseOptionalAmount(this.buyNowInput.value);
    if (startingBid === null || buyNowPrice === null) return null;
    return { sourceSlot: Number(cell), startingBid, buyNowPrice };
  }

  /** Choose the bag cell to list. The panel's own writes go through here so a
   *  caller writes the same state a click does, `StallBuilder.setPrice`'s
   *  rule. Returns false when that cell is not on offer. */
  setSourceSlot(slot: number): boolean {
    const wanted = String(slot);
    for (const option of Array.from(this.pick.options)) {
      if (option.value === wanted) {
        this.pick.value = wanted;
        return true;
      }
    }
    return false;
  }

  /** Set the two prices. Empty buy-now means no buy-now, which is a legal
   *  answer -- see `parseOptionalAmount`. */
  setPrices(startingBid: string, buyNowPrice: string): void {
    this.startInput.value = startingBid;
    this.buyNowInput.value = buyNowPrice;
  }

  private paintBag(): void {
    const bag = this.bag;
    const previous = this.pick.value;
    this.pick.replaceChildren();
    const doc = this.root.ownerDocument;
    // `bag.slots` is the *occupied* cells and each one carries its own cell
    // number, which is the number that goes on the wire. Never this list's
    // index: a bag is sparse and the two part company the moment anything is
    // used out of the middle of it.
    const cells = bag === null ? [] : bag.slots.filter((slot) => auctionable(slot));
    if (cells.length === 0) {
      const none = doc.createElement("option");
      none.value = "";
      none.textContent = "nothing to sell";
      this.pick.append(none);
      this.listButton.disabled = true;
      return;
    }
    for (const slot of cells) {
      const option = doc.createElement("option");
      option.value = String(slot.slot);
      option.textContent =
        slot.count > 1 ? `${slot.name} x${slot.count}` : slot.name;
      this.pick.append(option);
    }
    this.pick.value = cells.some((slot) => String(slot.slot) === previous)
      ? previous
      : String(cells[0].slot);
    this.listButton.disabled = false;
  }

  private submitListing(): void {
    const draft = this.draft;
    if (draft === null) return;
    const { sourceSlot: cell, startingBid: starting, buyNowPrice: buyNow } = draft;
    this.actions.onList?.(cell, starting, buyNow);
  }
}
